import type {
  CreateHelpMessageDto,
  CreateHelpRequestDto,
  HelpRequestWithDetails,
} from "@reactive-resume/dto";
import { axios } from "@/client/libs/axios";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  CENTRE D'AIDE — client API (contrats : libs/dto/src/help)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Le backend garantit l'ownership systématique (routes /help/requests/*).
 *  Types dérivés du contrat unique HelpRequestWithDetails — aucune
 *  dépendance directe à @prisma/client côté client.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export type HelpRequestRow = HelpRequestWithDetails;
export type HelpMessage = HelpRequestWithDetails["messages"][number];
export type HelpStatus = HelpRequestWithDetails["status"];
export type HelpCategoryKey = HelpRequestWithDetails["category"];

/** Liste des demandes de l'utilisateur (messages inclus, plus récentes d'abord). */
export const fetchHelpRequests = async (): Promise<HelpRequestRow[]> => {
  const response = await axios.get<HelpRequestRow[]>("/help/requests");
  return response.data;
};

/** Détail d'une demande (ownership côté serveur, 404 sinon). */
export const fetchHelpRequest = async (id: string): Promise<HelpRequestRow> => {
  const response = await axios.get<HelpRequestRow>(`/help/requests/${id}`);
  return response.data;
};

/** Crée une demande. 409 si une autre demande est déjà ouverte. */
export const createHelpRequest = async (dto: CreateHelpRequestDto): Promise<HelpRequestRow> => {
  const response = await axios.post<HelpRequestRow>("/help/requests", dto);
  return response.data;
};

/** Ajoute un message au fil. Interdit sur une demande CLOSED (409). */
export const sendHelpMessage = async (
  id: string,
  dto: CreateHelpMessageDto,
): Promise<HelpMessage> => {
  const response = await axios.post<HelpMessage>(`/help/requests/${id}/messages`, dto);
  return response.data;
};

/** Clôture sa demande (définitif). */
export const closeHelpRequest = async (id: string): Promise<HelpRequestRow> => {
  const response = await axios.patch<HelpRequestRow>(`/help/requests/${id}/close`);
  return response.data;
};

/** Réouvre une demande clôturée/résolue (le problème est réapparu). */
export const reopenHelpRequest = async (id: string): Promise<HelpRequestRow> => {
  const response = await axios.patch<HelpRequestRow>(`/help/requests/${id}/reopen`);
  return response.data;
};

/**
 * Extrait un message d'erreur lisible d'une réponse axios
 * (NestJS : { message: string | string[] }, sinon fallback).
 */
export const helpApiError = (error: unknown, fallback: string): string => {
  const payload = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(payload)) return payload[0] ?? fallback;
  return payload ?? fallback;
};
